import { gql } from "@apollo/client";
import { findRecommendedPosition } from "@asmbl/shared/comp-recommendation";
import { FeatureFlag } from "@asmbl/shared/feature-flags";
import makeStyles from "../../../../makeStylesV4Compat";
import clsx from "clsx";
import { CalendarIcon } from "src/components/AssembleIcons/Extra-Small/CalendarIcon";
import { ClockIcon } from "src/components/AssembleIcons/Extra-Small/ClockIcon";
import { useAuth } from "src/components/Auth/AuthContext";
import { useCompStructure } from "src/components/CompStructureContext";
import { useFeatureFlags } from "src/components/FeatureContext";
import { GRAY_11 } from "src/theme";
import {
  CondensedTableNewPositionCell2_participant as Participant,
  PayPeriodType,
} from "../../../../__generated__/graphql";
import { AssembleTruncatedTypography } from "../../../../components/AssembleTruncatedTypography";
import { ColumnComponent2 } from "../CondensedTable/CondensedTableInner";
import { ColumnIds, ColumnIdsToHeaders } from "../Contexts/ColumnOrderContext";
import { useTableData } from "../Contexts/TableDataContext2";
import { XLG_COL_WIDTH } from "./dimensions";

type Props = {
  row: { original: Participant };
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  rowActedOn: {
    "& path": {
      stroke: `${GRAY_11} !important`,
    },
  },
  icon: {
    flex: "none",
  },
}));

export function CondensedTableNewPositionCell2({
  row: { original: employee },
}: Props): JSX.Element {
  const { availablePositions } = useTableData();
  const { compStructure } = useCompStructure();
  const { isEnabled } = useFeatureFlags();
  const { userId } = useAuth();
  const classes = useStyles();
  const recItems = employee.compRecommendation?.latestSubmittedItems ?? [];
  const newPosition = findRecommendedPosition(recItems, availablePositions);
  const oldPosition = employee.subject.activeEmployment?.position?.name;
  const label = newPosition?.name ?? oldPosition ?? "-";

  const showHourly = Boolean(
    compStructure?.allowHourlyEmployees === true &&
      isEnabled(FeatureFlag.HourlyEmployees) &&
      (newPosition?.name != null || oldPosition)
  );
  const positionType =
    newPosition?.type ?? employee.subject.activeEmployment?.payPeriod;

  const currUserIsAuthor = employee.lastActivity?.author.id === userId;

  return (
    <div className={clsx(classes.root, currUserIsAuthor && classes.rowActedOn)}>
      {showHourly && (
        <>
          {positionType === PayPeriodType.ANNUAL && (
            <CalendarIcon className={classes.icon} />
          )}
          {positionType === PayPeriodType.HOURLY && (
            <ClockIcon className={classes.icon} />
          )}
        </>
      )}
      <AssembleTruncatedTypography lines={1} title={label}>
        {label}
      </AssembleTruncatedTypography>
    </div>
  );
}

CondensedTableNewPositionCell2.fragments = {
  participant: gql`
    fragment CondensedTableNewPositionCell2_participant on CompCycleParticipant {
      subject {
        activeEmployment {
          id
          payPeriod
          position {
            id
            name
            type
          }
        }
      }
      lastActivity {
        submittedAt
        type
        author {
          id
        }
      }
      compRecommendation(skipEligibility: $skipEligibility) {
        subjectId
        compCycleId
        latestSubmittedItems {
          id
          recommendationType
          recommendedPosition {
            id
          }
        }
      }
    }
  `,
  position: gql`
    fragment CondensedTableNewPositionCell2_position on Position {
      id
      name
      type
    }
  `,
};

CondensedTableNewPositionCell2.Header = ColumnIdsToHeaders.get(
  ColumnIds.NEW_POSITION
);
CondensedTableNewPositionCell2.id = ColumnIds.NEW_POSITION;

const column: ColumnComponent2["column"] = {
  Cell: CondensedTableNewPositionCell2,
  Header: CondensedTableNewPositionCell2.Header,
  id: CondensedTableNewPositionCell2.id,
  width: XLG_COL_WIDTH,
};
CondensedTableNewPositionCell2.column = column;
